<template>
  <div class="container text-center py-5">
    <div v-show="isLoading" class="loader-container">
      <div ref="lottieLoader" class="lottie-animation"></div>
      <p class="loader-text">Uploading your memories…</p>
    </div>

    <!-- ===== Photo Album (Uploader) ===== -->
    <div ref="bienvenidaRef" id="album" class="card shadow-lg p-4 bg-light upload-container">
      <!-- Visual header -->
      <div class="mb-3 encabezado-visual">
        <img src="@/assets/camara.png" alt="Camera" class="img-fluid gif-pequeno brillo-icono">
      </div>

      <h4 class="titulo">Memory Album</h4>
      <p class="lead-subtitle">
        <span class="resaltado">Help me save the magic!</span> Upload your favorite event photos here
        so no one forgets the most special moments.
      </p>

      <!-- Tips & badges -->
      <div class="badges">
        <span class="badge-info"><i class="fas fa-shield-alt"></i> Private & secure</span>
        <span class="badge-info"><i class="fas fa-images"></i> Up to 10 images per upload</span>
        <span class="badge-info"><i class="far fa-file-image"></i> JPG · PNG · GIF</span>
      </div>

      <!-- Upload area -->
      <label for="inputFile" class="upload-area" aria-label="Select images to upload">
        <i class="fas fa-camera-retro upload-icon"></i>
        <div class="upload-textos">
          <span class="custom-file-upload">Click to select</span>
          <small class="subtexto">or drag and drop your photos here</small>
        </div>
      </label>

      <input 
        id="inputFile"
        ref="fileInput"
        type="file"
        name="fotos"
        class="file-input"
        accept="image/*"
        multiple
        @change="validateFiles"
      >

      <p class="nota-ayuda">
        Tip: choose sharp, well-lit images so the album looks amazing ✨
      </p>

      <p v-if="errorMessage" class="text-danger mensaje-error">
        <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
      </p>

      <!-- Preview -->
      <div v-if="previewImages.length > 0" class="preview-container">
        <div class="preview-header">
          <h5 class="text-secondary mt-3">Your selected photos <span class="contador">({{ previewImages.length }})</span></h5>
          <small class="preview-tip">You can remove any before uploading.</small>
        </div>
        <div class="preview-grid">
          <div
            v-for="(src, index) in previewImages"
            :key="index"
            class="preview-item"
          >
            <img :src="src" alt="Preview" class="preview-image">
            <button class="delete-btn" @click="removeImage(index)" aria-label="Remove image">
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Main action -->
      <button class="upload-btn" @click="confirmUpload">
        <i class="fas fa-cloud-upload-alt"></i> Upload photos now
      </button>

      <p class="text-muted mt-3 recordatorio">
        Review your selection and then click <strong>“Upload photos now”</strong>.
      </p>

      <p v-if="photoUploaded" class="mensaje-gracias">
        <i class="far fa-heart"></i> Thank you for sharing your photos with us!
      </p>

      <!-- Trust badges -->
      <div class="sellos">
        <span class="sello"><i class="fas fa-lock"></i> Encrypted</span>
        <span class="sello"><i class="fas fa-check-circle"></i> Verified upload</span>
        <span class="sello"><i class="fas fa-star"></i> Optimized quality</span>
      </div>
    </div>

    <!-- ===== Event Gallery (photos only) ===== -->
    <section class="galeria-wrapper">
      <div class="galeria-header">
        <h5 class="galeria-titulo"><i class="fas fa-images"></i> Event photos</h5>
        <small v-if="totalFotos !== null" class="galeria-sub">{{ totalFotos }} photos</small>
      </div>

      <!-- Empty state -->
      <div v-if="!galeriaLoading && fotos.length === 0" class="galeria-empty">
        <i class="far fa-image"></i>
        <p>There are no photos yet. Be one of the first to upload! 🥰</p>
      </div>

      <!-- Skeleton / Loading -->
      <div v-if="galeriaLoading && fotos.length === 0" class="galeria-grid">
        <div v-for="n in 8" :key="'sk'+n" class="galeria-item skeleton"></div>
      </div>

      <!-- Grid -->
      <div v-if="fotos.length > 0" class="galeria-grid">
        <figure
          v-for="foto in fotos"
          :key="foto.id || foto.url"
          class="galeria-item"
          @click="openLightbox(foto)"
          :title="'Event photo'"
        >
          <img
            :src="foto.url"
            alt="Event photo"
            loading="lazy"
            class="galeria-img"
            @error="e => e.target.style.display = 'none'"
          />
        </figure>
      </div>

      <!-- Load more (disabled because /api2/subirfotos has no pagination) -->
      <div v-if="hasMore && !galeriaLoading" class="galeria-actions">
        <button class="btn-more" @click="loadMore"><i class="fas fa-plus-circle"></i> Load more</button>
      </div>

      <div v-if="galeriaLoading && fotos.length > 0" class="galeria-loading-inline">
        <i class="fas fa-spinner fa-spin"></i> Loading more photos…
      </div>
    </section>

    <!-- Lightbox -->
    <div v-if="lightboxOpen" class="lightbox" @click.self="closeLightbox">
      <button class="lightbox-close" @click="closeLightbox" aria-label="Close"><i class="fas fa-times"></i></button>
      <img :src="lightboxFoto?.url" class="lightbox-img" alt="Event photo" />
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, nextTick } from 'vue';
import axios from 'axios';
import baseUrl from '../api';
import Swal from 'sweetalert2';
import lottie from 'lottie-web';
import heartAnimation from '../assets/heart.json';

export default {
  name: 'hashtag-etiqueta',
  props: {
    idInvitado: {
      type: [Number, String],
      required: false,
      default: null,
      validator(v) {
        // allow null/'' or a number > 0
        if (v === null || v === '') return true;
        const n = Number(v);
        return !Number.isNaN(n) && n > 0;
      }
    },
    nombre: {
      type: String,
      default: '',
    },
    idFestejado: {
      type: Number,
      default: null,
    }
  },

  setup(props) {
    const errorMessage = ref('');
    const fileInput = ref(null);
    const selectedFiles = ref([]);
    const previewImages = ref([]);
    const photoUploaded = ref(false);
    const isLoading = ref(false);
    const lottieLoader = ref(null);
    const MAX_FILES = 10;
    const bienvenidaRef = ref(null);

    // ======= Gallery State =======
    const fotos = ref([]);            // [{ id, url }]
    const galeriaLoading = ref(false);
    const hasMore = ref(false);       // /api2/subirfotos does not paginate -> false
    const totalFotos = ref(null);
    const lightboxOpen = ref(false);
    const lightboxFoto = ref(null);

    onMounted(() => {
      nextTick(() => {
        setTimeout(() => {
          if (window.location.hash === '#album' && bienvenidaRef.value) {
            const offset = 100;
            const top = bienvenidaRef.value.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        }, 1000);
      });

      // Load gallery on mount (via /api2/subirfotos?id_festejada=)
      fetchGaleria({ reset: true });
    });

    // Lottie Loader
    watch(isLoading, (newVal) => {
      if (newVal && lottieLoader.value) {
        lottie.loadAnimation({
          container: lottieLoader.value,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: heartAnimation
        });
      }
    });

    const validateFiles = (event) => {
      const files = Array.from(event.target.files);

      if (files.length === 0) {
        errorMessage.value = 'Please select at least one image.';
        return;
      }

      if (files.length > MAX_FILES) {
        Swal.fire('Limit exceeded', `You can upload a maximum of ${MAX_FILES} images.`, 'warning');
        event.target.value = '';
        return;
      }

      const invalidFiles = files.filter(file => !file.type.startsWith('image'));
      if (invalidFiles.length > 0) {
        errorMessage.value = 'Please select image files only (JPG, PNG, GIF).';
        event.target.value = '';
        selectedFiles.value = [];
        previewImages.value = [];
      } else {
        errorMessage.value = '';
        selectedFiles.value = files;
        previewImages.value = selectedFiles.value.map(file => URL.createObjectURL(file));
      }
    };

    const removeImage = (index) => {
      selectedFiles.value.splice(index, 1);
      previewImages.value.splice(index, 1);
    };

    const confirmUpload = async () => {
      if (selectedFiles.value.length === 0) {
        errorMessage.value = 'Please select at least one image.';
        return;
      }

      Swal.fire({
        title: 'Ready to upload your memories?',
        text: 'Review your selection. You can remove any image before continuing.',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Upload photos now',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          uploadPhotos();
        }
      });
    };

    const uploadPhotos = async () => {
      isLoading.value = true;

      const formData = new FormData();
      selectedFiles.value.forEach(file => {
        formData.append('fotos', file);
      });

      formData.append('idFestejada', props.idFestejado);
      formData.append('idInvitado', props.idInvitado || null);

      try {
        if (!props.nombre && !props.idFestejado && !props.idInvitado) {
          console.error("ERROR: No valid parameters received.");
          return;
        }

        const params = [];
        if (props.nombre) params.push(encodeURIComponent(props.nombre));
        if (props.idFestejado) params.push(props.idFestejado);
        if (props.idInvitado) params.push(props.idInvitado);

        const paqueteUrl = `${baseUrl}/subirfotos/${params.join('/')}`;

        const response = await axios.post(paqueteUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.success) {
          Swal.fire('Done!', 'Your photos have been uploaded successfully.', 'success');
          photoUploaded.value = true;
          previewImages.value = [];
          selectedFiles.value = [];
          fileInput.value.value = '';

          // 🔁 Refresh gallery
          await fetchGaleria({ reset: true });
        } else {
          Swal.fire('Error', 'There was a problem uploading the photos.', 'error');
        }
      } catch (error) {
        console.error('Error uploading photos:', error);
        console.error('Error code:', error.code);
        console.error('Server response:', error.response ? error.response.data : "No response");
        Swal.fire('Error', 'There was a problem uploading the photos.', 'error');
      } finally {
        isLoading.value = false;
      }
    };

    // === Load gallery from /api2/subirfotos?id_festejada= ===
    const fetchGaleria = async ({ reset = false } = {}) => {
      // Without id_festejada -> empty gallery
      if (!props.idFestejado || Number(props.idFestejado) <= 0) {
        fotos.value = [];
        totalFotos.value = 0;
        hasMore.value = false;
        return;
      }

      try {
        galeriaLoading.value = true;
        if (reset) fotos.value = [];

        const endpoint = `${baseUrl}/api4/subirfotos?id_festejada=${encodeURIComponent(String(props.idFestejado))}`;
        const resp = await fetch(endpoint, { credentials: 'include' });

        // If your backend returns 404 when there are no photos
        if (resp.status === 404) {
          fotos.value = [];
          totalFotos.value = 0;
          hasMore.value = false;
          return;
        }
        if (!resp.ok) {
          console.warn('fetchGaleria not OK:', resp.status);
          fotos.value = [];
          totalFotos.value = 0;
          hasMore.value = false;
          return;
        }

        const arr = await resp.json(); // [{ url_foto, (optional) id_subirfotos }]
        const nueva = Array.isArray(arr)
          ? arr.map((f, i) => ({
              id: f.id_subirfotos ?? i,
              url: f.url_foto || ''
            }))
          : [];

        fotos.value = nueva;
        totalFotos.value = nueva.length;
        hasMore.value = false; // no pagination in /api2
      } catch (e) {
        console.error('Error loading gallery:', e);
        fotos.value = [];
        totalFotos.value = 0;
        hasMore.value = false;
      } finally {
        galeriaLoading.value = false;
      }
    };

    const loadMore = async () => {
      // Disabled: the /api2/subirfotos endpoint does not paginate
      return;
    };

    const openLightbox = (foto) => {
      lightboxFoto.value = foto;
      lightboxOpen.value = true;
      document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
      lightboxOpen.value = false;
      lightboxFoto.value = null;
      document.body.style.overflow = '';
    };

    return { 
      errorMessage, 
      fileInput, 
      confirmUpload, 
      validateFiles, 
      photoUploaded, 
      bienvenidaRef,
      previewImages, 
      removeImage,
      isLoading, 
      lottieLoader,

      // gallery
      fotos,
      galeriaLoading,
      hasMore,
      totalFotos,
      loadMore,
      openLightbox,
      closeLightbox,
      lightboxOpen,
      lightboxFoto,
      fetchGaleria
    };
  }
};
</script>


<style scoped>
/* ===== Layout ===== */
.container{ min-height: 200px; height: auto; }
.upload-container {
  max-width: 860px; margin: auto; border-radius: 18px; background: #fff; position: relative; overflow: hidden;
  border: 1px solid rgba(0,0,0,0.04);
}

/* ===== Encabezado ===== */
.encabezado-visual { display: flex; justify-content: center; align-items: center; }
.gif-pequeno { width: 54px; height: auto; filter: drop-shadow(0 4px 10px rgba(0,0,0,.08)); }
.brillo-icono { animation: pulse-brillo 2.4s ease-in-out infinite; }
@keyframes pulse-brillo {
  0% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(234,173,192,0.0)); }
  50% { transform: scale(1.06); filter: drop-shadow(0 0 12px rgba(234,173,192,0.5)); }
  100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(234,173,192,0.0)); }
}

/* ===== Títulos y textos ===== */
.titulo{ font-size: 58px !important; color: #efa4ec;   font-family: 'britishgruesa', sans-serif;
 margin-bottom: 4px; letter-spacing: .2px; }




.lead-subtitle{ color: #5b6168; font-size: 16px; line-height: 1.55; margin-bottom: 12px; }
.resaltado{ color: #2b2d2e; font-weight: 700; }

/* Badges */
.badges{ display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; margin-bottom: 14px; }
.badge-info{
  background: #f6f7f9; color: #3a3f45; border: 1px solid #eceff3; border-radius: 999px; padding: 6px 12px; font-size: 12px;
  display: inline-flex; align-items: center; gap: 6px;
}

/* ===== Área de subida ===== */
.upload-area {
  border: 2px dashed #2b24174e; padding: 28px 20px; text-align: center; cursor: pointer; border-radius: 14px; transition: all 0.35s ease;
  background: linear-gradient(180deg, rgba(255,255,255,1), rgba(250,250,252,0.6)); position: relative; overflow: hidden;
}
.upload-area:hover {
  transform: translateY(-1px); box-shadow: 0 12px 30px rgba(234,173,192,0.16);
  background: linear-gradient(180deg, rgba(255,255,255,1), rgba(255,245,249,0.65));
}
.upload-icon { font-size: 40px; color: #2b2d2e79; margin-right: 8px; }
.upload-textos{ display: inline-flex; flex-direction: column; align-items: center; }
.custom-file-upload { font-weight: 700; color: #2b2d2e; }
.subtexto{ font-size: 12px; color: #7a7f85; margin-top: 4px; }

/* Nota */
.nota-ayuda{ color: #7a7f85; font-size: 13px; margin-top: 10px; }

/* Mensaje de error */
.mensaje-error{ margin-top: 10px; font-weight: 600; }

/* ===== Input real (oculto visualmente) ===== */
.file-input { background-color: white; color: white; border: none; width: 1px; height: 1px; opacity: 0.01; position: absolute; pointer-events: none; }

/* ===== Vista previa ===== */
.preview-container{ margin-top: 16px; }
.preview-header{ display: flex; align-items: baseline; justify-content: center; gap: 10px; }
.contador{ color: #efa4ec; font-weight: 700; }
.preview-tip{ color:#8a9096; font-size: 12px; }

.preview-grid { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; }
.preview-item {
  position: relative; width: 104px; height: 104px; border-radius: 12px; overflow: hidden; background: #f4f5f7; border: 1px solid rgba(0,0,0,0.04);
  transition: transform .2s ease, box-shadow .2s ease;
}
.preview-item:hover{ transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
.preview-image { width: 100%; height: 100%; object-fit: cover; }
.delete-btn {
  position: absolute; top: 6px; right: 6px; background: #e74c3c; color: white; border: none; border-radius: 50%; width: 22px; height: 22px; cursor: pointer;
  display: grid; place-items: center; font-size: 12px; box-shadow: 0 2px 10px rgba(231,76,60,0.35); transition: transform .15s ease;
}
.delete-btn:hover{ transform: scale(1.06); }

/* ===== Botón principal ===== */
.upload-btn {
  background-color: #cb7ee4; color: white; font-size: 16px; font-weight: bold; padding: 12px 20px; justify-content: center; border: none; border-radius: 10px; cursor: pointer;
  transition: all 0.25s ease-in-out; display: inline-flex; align-items: center; gap: 8px; box-shadow: 0px 6px 16px rgba(234,173,192,0.38); margin-top: 8px;
}
.upload-btn:hover { transform: translateY(-1px); box-shadow: 0px 10px 22px rgba(234,173,192,0.5); background: linear-gradient(135deg, #eaadc0, #d69bb0); }
.upload-btn i { font-size: 20px; }

.recordatorio{ font-size: 13px; }

/* Mensaje de gracias */
.mensaje-gracias{ margin-top: 10px; color: #2b2d2e; font-weight: 700; }

/* Sellos de confianza */
.sellos{ display: flex; gap: 14px; justify-content: center; margin-top: 12px; flex-wrap: wrap; }
.sello{ font-size: 12px; color: #6c7279; background: #fafbfc; border: 1px solid #eef1f4; border-radius: 999px; padding: 6px 12px; display: inline-flex; align-items: center; gap: 6px; }

/* ===== Loader a pantalla completa ===== */
.loader-container {
  position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 10000 !important; width: 100vw; height: 100vh;
  background: rgba(17, 18, 20, 0.76); display: flex; justify-content: center; align-items: center; flex-direction: column;
}
.lottie-animation { width: 150px; height: 150px; }
.loader-text { color: white; font-size: 20px; font-weight: bold; font-family: 'Dancing Script', cursive; margin-top: 15px; letter-spacing: .3px; }

/* ======= Galería ======= */
.galeria-wrapper{ max-width: 1100px; margin: 28px auto 0; padding: 0 8px; }
.galeria-header{ display: flex; align-items: baseline; gap: 12px; justify-content: center; margin-bottom: 10px; }
.galeria-titulo{ font-size: 20px; color: #2b2d2e; font-weight: 800; margin: 0; }
.galeria-sub{ color: #8a9096; }
.galeria-empty{ padding: 24px 8px; color: #7a7f85; }
.galeria-empty i{ font-size: 34px; margin-bottom: 8px; display: block; }

.galeria-grid{ display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; }
@media (max-width: 992px){ .galeria-grid{ grid-template-columns: repeat(4, 1fr);} }
@media (max-width: 576px){ .galeria-grid{ grid-template-columns: repeat(3, 1fr);} }

.galeria-item{
  position: relative; border-radius: 12px; overflow: hidden; background: #f4f5f7; border: 1px solid rgba(0,0,0,0.04);
  cursor: zoom-in; transition: transform .2s ease, box-shadow .2s ease; min-height: 120px;
}
.galeria-item:hover{ transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
.galeria-img{ width: 100%; height: 100%; object-fit: cover; display: block; }

.skeleton{ background: linear-gradient(90deg,#f0f2f5 25%,#f6f7f9 37%,#f0f2f5 63%); background-size: 400% 100%; animation: shimmer 1.2s infinite linear; }
@keyframes shimmer{ 0%{background-position: 100% 0} 100%{background-position: -100% 0} }

.galeria-actions{ text-align: center; margin-top: 14px; }
.btn-more{
  background: #2b2d2e; color: #fff; border: 0; font-weight: 700; border-radius: 10px; padding: 10px 16px; cursor: pointer;
  transition: transform .15s ease, box-shadow .2s ease, background .2s ease;
}
.btn-more:hover{ transform: translateY(-1px); box-shadow: 0 10px 22px rgba(0,0,0,.12); background: #212325; }
.galeria-loading-inline{ margin-top: 12px; color: #7a7f85; }

/* ===== Lightbox ===== */
.lightbox{ position: fixed; inset: 0; background: rgba(13,14,16,.85); display: grid; place-items: center; z-index: 10001; padding: 16px; }
.lightbox-img{ max-width: 92vw; max-height: 80vh; object-fit: contain; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,.45); }
.lightbox-close{
  position: fixed; top: 14px; right: 14px; width: 36px; height: 36px; border-radius: 999px; background: rgba(255,255,255,.15); border: none; color: #fff;
  cursor: pointer; display: grid; place-items: center; transition: background .15s ease;
}
.lightbox-close:hover{ background: rgba(255,255,255,.25); }

/* ===== Pequeños toques responsivos ===== */
@media (max-width: 576px){
  .titulo{ font-size: 52px !important; }
  .preview-item{ width: 96px; height: 96px; }
}
</style>
